import React, { useEffect, useState } from 'react'
import { Grid, Skeleton, Box, Chip, CircularProgress, IconButton, Tooltip } from "@mui/material"
import { ButtonLoadingSeeker, IconSeeker } from "@ui-seeker/core"
import { getExcel, getSubmit } from "@ui-seeker/utils"
import PageWrapper from "Pages/@PageWrapper"
import { AppCrudTable, AppAntSwitch, AppButtonIcon } from "@components"
import { BACKEND_APIS } from "@constants"
import { AUTO_EUROPE_TOOL_ROUTES } from "@constants/routes"
import {
	autoEuropeDrawerData,
	autoEuropeListData,
	autoEuropeStatusFilter,
	autoEuropeIsLoading,
	autoEuropeIsSubmitting,
	autoEuropeOriginalData,
} from "Pages/AutoEuropeTool/Page/@signals"
import AutoEuropeDrawer from "Pages/AutoEuropeTool/Page/Drawer"
import useAutoEuropeTable from "Pages/AutoEuropeTool/Page/@hooks/useTable"
import useAutoEuropeFormSubmit from "Pages/AutoEuropeTool/Page/@hooks/useFormSubmit"
import useAutoEuropeActions from "Pages/AutoEuropeTool/Page/@hooks/useActions"
import useAutoEuropeStatus from "Pages/AutoEuropeTool/Page/@hooks/useStatus"
import useAutoEuropeEdit from "Pages/AutoEuropeTool/Page/@hooks/useEdit"
import useAutoEuropeClone from "Pages/AutoEuropeTool/Page/@hooks/useClone"
import ReportsNameCell from "Pages/AutoEuropeTool/Page/@components/ReportsNameCell"
import ProviderOverrideModal from "Pages/AutoEuropeTool/Page/@components/ProviderOverrideModal"
import ConfirmEditModal from "Pages/AutoEuropeTool/Page/@components/ConfirmEditModal"
import SimulateModal from "Pages/AutoEuropeTool/Page/@components/SimulateModal"
import BulkSeasonSetToolbar from "Pages/AutoEuropeTool/Page/@components/BulkSeasonSetToolbar"

function StatusSwitch({ value, row }) {
	const { handleStatus } = useAutoEuropeStatus(row.id, row.name)
	return (
		<Box sx={{ display: 'flex', justifyContent: 'center' }}>
			<AppAntSwitch checked={value} onChange={() => handleStatus(!value)}/>
		</Box>
	)
}

function ActionsCell({ row, onDelete, onSimulate }) {
	const { onEdit, isLoading: isEditLoading } = useAutoEuropeEdit(row.id)
	const { onClone, isLoading: isCloneLoading } = useAutoEuropeClone(row.id)
	return (
		<Box sx={{ display: 'flex', justifyContent: 'center' }}>
			<AppButtonIcon icon="play" tooltip="Simulate" onClick={e => { e.stopPropagation(); onSimulate(row.id) }}/>
			{isEditLoading
				? <CircularProgress size={17} sx={{ m: 0.2 }}/>
				: <AppButtonIcon icon="pen" tooltip="Edit" onClick={e => { e.stopPropagation(); onEdit() }}/>
			}
			{isCloneLoading
				? <CircularProgress size={17} sx={{ m: 0.2 }}/>
				: <AppButtonIcon icon="clone" tooltip="Clone" onClick={e => { e.stopPropagation(); onClone() }}/>
			}
			<AppButtonIcon
				icon="trash-can"
				tooltip="Delete"
				onClick={e => { e.stopPropagation(); onDelete(row.id, row.name) }}
			/>
		</Box>
	)
}

const centered = v => <Box sx={{ display: 'flex', justifyContent: 'center' }}>{v}</Box>

const valueFilterLogic = (value, filters) => {
	if (!filters?.length) return false
	const v = value?.value ?? value
	return !filters.includes(String(v))
}

const valueFilterOptions = (data, field) => ({
	names: [...new Set(data.map(row => row?.[field]?.value).filter(v => v != null))]
		.sort((a, b) => a - b)
		.map(String),
	logic: valueFilterLogic,
})

const undercutLabel = v => v?.value != null ? `${v.value} ${v.type === 0 ? '€' : '%'}` : null

const undercutFilterOptions = data => ({
	names: [...new Set(data.map(row => undercutLabel(row?.undercut)).filter(Boolean))],
	logic: (value, filters) => {
		if (!filters?.length) return false
		return !filters.includes(undercutLabel(value))
	},
})

const arrayTextFilterLogic = (arr, filters) => {
	if (!filters?.[0]) return false
	if (!Array.isArray(arr) || arr.length === 0) return true
	const q = String(filters[0]).toLowerCase()
	return !arr.some(item => String(item).toLowerCase().includes(q))
}

const rangeLabel = (r, fromKey, toKey) => `${r[fromKey]} → ${r[toKey]}`

const rangeFilterOptions = (data, field, fromKey, toKey) => ({
	names: [...new Set(data.flatMap(row => (row?.[field] || []).map(r => rangeLabel(r, fromKey, toKey))))],
	logic: (arr, filters) => {
		if (!filters?.length) return false
		if (!Array.isArray(arr) || arr.length === 0) return true
		return !arr.some(r => filters.includes(rangeLabel(r, fromKey, toKey)))
	},
})

function OverridesButton({ overrides, title, isUndercut, onClick }) {
	if (!Array.isArray(overrides) || overrides.length === 0) return null
	return (
		<Tooltip title={`${overrides.length} override${overrides.length > 1 ? 's' : ''}`}>
			<IconButton size="small" onClick={e => { e.stopPropagation(); onClick() }} sx={{ ml: 0.5, p: 0.25 }}>
				<IconSeeker icon="plus" fontSize="small"/>
			</IconButton>
		</Tooltip>
	)
}

export default function AutoEuropePricingToolPage() {
	const { getTableData } = useAutoEuropeTable()
	const { onSubmit } = useAutoEuropeFormSubmit({ getTableData })
	const { onDisable, onDelete } = useAutoEuropeActions()
	const [viewModal, setViewModal] = useState(null)
	const [confirmEdit, setConfirmEdit] = useState(null)
	const [simulatePricingToolId, setSimulatePricingToolId] = useState(null)
	const [seasonSetsMap, setSeasonSetsMap] = useState({})

	const handleSubmit = (data) => {
		if (data.id) {
			setConfirmEdit({ data, original: autoEuropeOriginalData.peek() })
		} else {
			onSubmit(data)
		}
	}

	useEffect(() => {
		getTableData()
		getSubmit(`${BACKEND_APIS.AUTOEUROPE_PRICING_TOOL}/season-sets/list`)
			.then(list => {
				const map = {}
				;(list || []).forEach(s => { map[s.id] = s.name })
				setSeasonSetsMap(map)
			})
			.catch(() => {})
	}, [])

	return <PageWrapper
		head={`PS Rentacar | ${AUTO_EUROPE_TOOL_ROUTES.SUBTITLE}`}
		title={AUTO_EUROPE_TOOL_ROUTES.TITLE}
		subtitle={AUTO_EUROPE_TOOL_ROUTES.SUBTITLE}
	>
		<AutoEuropeDrawer onSubmit={handleSubmit} isLoading={autoEuropeIsSubmitting.value}/>
		{confirmEdit && (
			<ConfirmEditModal
				open
				original={confirmEdit.original}
				current={confirmEdit.data}
				isLoading={autoEuropeIsSubmitting.value}
				onClose={() => setConfirmEdit(null)}
				onConfirm={() => { onSubmit(confirmEdit.data); setConfirmEdit(null) }}
			/>
		)}
		{viewModal && (
			<ProviderOverrideModal
				open
				readOnly
				title={viewModal.title}
				overrides={viewModal.overrides}
				isUndercut={viewModal.isUndercut}
				withDurations={viewModal.withDurations}
				onClose={() => setViewModal(null)}
			/>
		)}
		{simulatePricingToolId && (
			<SimulateModal
				open
				pricingToolId={simulatePricingToolId}
				onClose={() => setSimulatePricingToolId(null)}
			/>
		)}
		<Grid container spacing={2}>
			<Grid item xs={12}>
				{autoEuropeIsLoading.value
					? <Skeleton variant="rectangular" height={500}/>
					: <AppCrudTable
						title="AutoEurope Pricing Tool"
						data={autoEuropeListData.value}
						columns={[
							{ name: 'name', label: 'Name', options: { filterType: 'textField', customBodyRowRender: v => centered(v) } },
							{ name: 'minimumMargin', label: 'Min. Margin (%)', options: { filterType: 'dropdown', filterOptions: valueFilterOptions(autoEuropeListData.value, 'minimumMargin'), customBodyRowRender: v => centered(<Box sx={{ display: 'flex', alignItems: 'center' }}>{v?.value ?? v}<OverridesButton overrides={v?.providers} title="Min. Margin overrides" onClick={() => setViewModal({ title: 'Min. Margin overrides', overrides: v.providers, isUndercut: false, withDurations: true })}/></Box>) } },
							{ name: 'minimumProfit', label: 'Min. Profit (€)', options: { filterType: 'dropdown', filterOptions: valueFilterOptions(autoEuropeListData.value, 'minimumProfit'), customBodyRowRender: v => centered(<Box sx={{ display: 'flex', alignItems: 'center' }}>{v?.value ?? v}<OverridesButton overrides={v?.providers} title="Min. Profit overrides" onClick={() => setViewModal({ title: 'Min. Profit overrides', overrides: v.providers, isUndercut: false, withDurations: true })}/></Box>) } },
							{ name: 'seasonSetId', label: 'Seasons', options: { filterType: 'dropdown', filterOptions: { names: [...new Set(Object.values(seasonSetsMap))].sort(), logic: (v, filters) => filters.length ? !filters.includes(seasonSetsMap[v] ?? '') : false }, customBodyRowRender: v => centered(v && seasonSetsMap[v] ? <Chip label={seasonSetsMap[v]} size="small" color="primary" sx={{ m: 0.25 }}/> : '-') } },
							{ name: 'durations', label: 'Durations', options: { filterType: 'dropdown', filterOptions: rangeFilterOptions(autoEuropeListData.value, 'durations', 'durationFrom', 'durationTo'), customBodyRowRender: v => centered(Array.isArray(v) && v.length > 0 ? <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>{v.map((d, i) => <span key={i}>{`${d.durationFrom} → ${d.durationTo}`}</span>)}</Box> : '-') } },
							{ name: 'undercut', label: 'Undercut', options: { filterType: 'dropdown', filterOptions: undercutFilterOptions(autoEuropeListData.value), customBodyRowRender: v => centered(<Box sx={{ display: 'flex', alignItems: 'center' }}>{v?.value != null ? `${v.value} ${v.type === 0 ? '€' : '%'}` : '-'}<OverridesButton overrides={v?.providers} title="Undercut overrides" onClick={() => setViewModal({ title: 'Undercut overrides', overrides: v.providers, isUndercut: true })}/></Box>) } },
							{ name: 'agencyId', label: 'Agency ID', options: { customBodyRowRender: v => centered(v) } },
							{ name: 'agentId', label: 'Agent ID', options: { customBodyRowRender: v => centered(v) } },
							{ name: 'reportsName', label: 'Reports', options: { filterType: 'textField', filterOptions: { logic: arrayTextFilterLogic }, customBodyRowRender: v => <ReportsNameCell value={v}/> } },
							{
								name: 'enabled', label: 'Off/On',
								options: {
									display: 'excluded',
									filterType: 'checkbox',
									filterList: autoEuropeStatusFilter.value,
									customBodyRowRender: v => v ? 'Enabled' : 'Disabled',
								}
							},
							{
								name: 'enabled', label: 'Off/On',
								options: {
									filter: false,
									customBodyRowRender: (v, row) => <StatusSwitch value={v} row={row}/>,
								}
							},
							{ name: 'id', label: 'Actions', options: { filter: false, customBodyRowRender: (_, row) => <ActionsCell row={row} onDelete={onDelete} onSimulate={setSimulatePricingToolId}/> } },
						]}
						options={{
							selectableRows: 'multiple',
							customToolbarSelect: (selectedData, setSelectedRows) => (
								<BulkSeasonSetToolbar
									selectedData={selectedData}
									setSelectedRows={setSelectedRows}
								/>
							),
							customToolbar: () => (
								<Box sx={{ ml: 4 }}>
									<ButtonLoadingSeeker
										startIcon={<IconSeeker icon="file-plus"/>}
										onClick={() => autoEuropeDrawerData.value = { name: null, minimumMargin: { value: 10, providers: [] }, minimumProfit: { value: 5, providers: [] }, undercut: { value: 2.5, type: 1, direction: 0, providers: [] }, agencyId: "*", agentId: "0", filterGroupOfficesExclusion: false, filterGroupSitesExclusion: false, filterGroupWebSuppliersExclusion: false, filterGroupProvidersExclusion: false, filterGroupWebSuppliers: [], filterAcrissExclusion: false, filterCategoriesExclusion: false, compareByCategory: false, targetPosition: null, insurance: null }}
									>
										Create
									</ButtonLoadingSeeker>
								</Box>
							),
							onFilterChange: (changedColumn, filterList, _, dataIdx) => {
								if (changedColumn === 'enabled')
									autoEuropeStatusFilter.value = filterList[dataIdx]
							},
							onDownload: () => getExcel(`${BACKEND_APIS.AUTOEUROPE_PRICING_TOOL}/list/excel`, {}),
						}}
						rowActions={row => [
							row.enabled && { label: 'Disable', icon: 'ban', onClick: () => onDisable(row.id, row.name) },
							{ label: 'Delete', icon: 'trash', onClick: () => onDelete(row.id, row.name) },
						].filter(Boolean)}
					/>
				}
			</Grid>
		</Grid>
	</PageWrapper>
}